const HEADER_PREFIX = 'time,';

// Time tokens that are plain integers mean the model carries no calendar dates,
// mirroring what a `.mat` without `S.reftime` produces.
const INDEX_TOKEN = /^\d+$/;

/**
 * @function readRows
 * @description Splits the raw file into `[timeToken, x, y]` triples, skipping the optional
 * header. Rows whose coordinates are not finite numbers are kept as gap markers
 * (`x` and `y` both `null`) instead of being dropped, so the map preview can
 * break the line where they were. Both coordinates must be null: that is what
 * makes `dropXyNullRows` remove them from the canonical geometry, exactly as
 * dropping them here used to.
 * @param {string} text
 * **Called by:
 * @see {parseCoastlineCsvText}
 * @memberof module:ShoreS
 * @returns {Array<{timeToken: string, x: number|null, y: number|null}>}
 */
const readRows = (text) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
  const dataLines = lines[0]?.toLowerCase().startsWith(HEADER_PREFIX) ? lines.slice(1) : lines;

  return dataLines
    .map((line) => {
      const [timeToken, x, y] = line.split(',');
      const px = Number(x);
      const py = Number(y);
      const valid = Number.isFinite(px) && Number.isFinite(py);
      return { timeToken: timeToken?.trim(), x: valid ? px : null, y: valid ? py : null };
    })
    .filter((row) => row.timeToken);
};

const isGapMarker = (point) => point.x === null;

/**
 * @function resolveTimes
 * @description Turns every time token into either an integer index or a Date, depending on
 * whether *all* of them look like plain indices.
 * @param {string[]} tokens - The distinct time tokens, in file order.
 * **Called by:
 * @see {parseCoastlineCsvText}
 * @memberof module:ShoreS
 * @returns {{times: Map<string, Date|number>, hasTemporalData: boolean}}
 */
const resolveTimes = (tokens) => {
  const hasTemporalData = !tokens.every((token) => INDEX_TOKEN.test(token));
  const times = new Map();

  tokens.forEach((token) => {
    if (!hasTemporalData) {
      times.set(token, Number(token));
      return;
    }
    const date = new Date(token);
    if (Number.isNaN(date.getTime())) {
      throw new Error(
        `Data inválida no CSV: "${token}". Use uma data ISO (ex: 2019-01-31) ou índices inteiros na coluna time.`,
      );
    }
    times.set(token, date);
  });

  return { times, hasTemporalData };
};

/**
 * @function parseCoastlineCsvText
 * @description Parses a long-format coastline CSV (`time,x,y`, one row per point) into the
 * same per-timestep structure `makeTimeIndexedCoastline` builds from a `.mat`,
 * so both formats converge on the same GeoJSON and downstream contract.
 *
 * Vertex order inside a timestep follows the file, since it defines the
 * coastline's geometry. Timesteps themselves are sorted ascending, because the
 * detected date range is taken from the first and last one.
 *
 * @param {string} text - Raw file contents.
 * **Called by:
 * @see {parseShoreSModelFile}
 * @memberof module:ShoreS
 * @returns {{coastlineByTime: Array<{time: (Date|number), points: Array<{x: (number|null), y: (number|null)}>}>,
 *            hasTemporalData: boolean}}
 */
export const parseCoastlineCsvText = (text) => {
  const rows = readRows(text);

  const pointsByToken = new Map();
  rows.forEach(({ timeToken, x, y }) => {
    if (!pointsByToken.has(timeToken)) pointsByToken.set(timeToken, []);
    pointsByToken.get(timeToken).push({ x, y });
  });

  pointsByToken.forEach((points, token) => {
    if (points.every(isGapMarker)) pointsByToken.delete(token);
  });

  if (!pointsByToken.size) {
    throw new Error('Nenhuma linha válida encontrada no CSV. Esperado o formato "time,x,y", uma linha por ponto.');
  }

  const { times, hasTemporalData } = resolveTimes([...pointsByToken.keys()]);

  const coastlineByTime = [...pointsByToken.entries()]
    .map(([token, points]) => ({ time: times.get(token), points }))
    .sort((a, b) => a.time - b.time);

  return { coastlineByTime, hasTemporalData };
};
